import { useRef } from 'react'
import {
  FormDataConsumer,
  required,
  SelectInput,
  TextInput,
  UPDATE,
  useGetIdentity,
  useRecordContext,
} from 'react-admin'

import { VoucherCodeInput } from './input'
import { AdvancedDateTimeInput } from '../../components/AdvancedDateTimeInputs'
import AdvancedNumberInput from '../../components/AdvancedNumberInput'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import {
  VOUCHER_DISCOUNT_TYPE_MINUTE,
  VOUCHER_DISCOUNT_TYPE_PERCENTAGE,
  VOUCHER_DISCOUNT_TYPES,
  VOUCHER_TYPE_CAMPAIGN,
  VOUCHER_TYPE_PERSONAL,
  VOUCHER_TYPES,
} from '../../config/vouchers'
import { getRoundedNowJS, parseDateAsDateTime, validateEndDateAfterStartDate } from '../../utils/dates'
import { useCurrentAccountSelectors } from '../accounts/hooks'
import { FormDivider } from '../common'
import { AdvancedSimpleForm } from '../common/forms'
import OrganisationReferenceInput from '../organisations/input'
import UserReferenceInput from '../users/input'

const voucherTypeChoices = Object.entries(VOUCHER_TYPES).map(([k, v]) => ({ id: k, name: v }))
const voucherDiscountTypeChoices = Object.entries(VOUCHER_DISCOUNT_TYPES).map(([k, v]) => ({ id: k, name: v }))

const SEVEN_DAYS_HOURS = 24 * 7

const discountValueConfig = {
  [VOUCHER_DISCOUNT_TYPE_PERCENTAGE]: {
    minValue: 1,
    maxValue: 100,
    unit: '%',
  },
  [VOUCHER_DISCOUNT_TYPE_MINUTE]: {
    minValue: 1,
    maxValue: SEVEN_DAYS_HOURS,
    unit: 'mymove.units.time.hours',
  },
}

export const transformValues = ({ organisation_id, user_id, ...restValues }) =>
  restValues
    ? {
        ...restValues,
        user_id: restValues.type === VOUCHER_TYPE_PERSONAL ? user_id : null,
        organisation_id: restValues.type === VOUCHER_TYPE_PERSONAL ? organisation_id : null,
      }
    : restValues

const VoucherFormLayout = ({ type = UPDATE }) => {
  const { hasSingleOrganisation, singleOrganisationId } = useCurrentAccountSelectors()
  const now = useRef(getRoundedNowJS())
  const record = useRecordContext()

  const { identity } = useGetIdentity()
  if (!identity) return null
  const currentUserRestrictedToOrganisationId = identity.restricted_to_organisation_id

  const defaultValues = {
    start_date: now.current,
    type: currentUserRestrictedToOrganisationId ? VOUCHER_TYPE_PERSONAL : VOUCHER_TYPE_CAMPAIGN,
    discount_type: VOUCHER_DISCOUNT_TYPE_PERCENTAGE,
    organisation_id: currentUserRestrictedToOrganisationId || (hasSingleOrganisation ? singleOrganisationId : null),
  }
  const hasBeenUsed = record?.number_of_uses > 0

  return (
    <AdvancedSimpleForm defaultValues={defaultValues} toolbar={<BasicFormToolbar formType={type} />} type={type}>
      <VoucherCodeInput validate={required()} readOnly={type === UPDATE && hasBeenUsed} />

      <TextInput source="name" validate={required()} />

      <FormDivider />

      <SelectInput
        source="type"
        choices={voucherTypeChoices}
        validate={required()}
        readOnly={!!currentUserRestrictedToOrganisationId || type === UPDATE}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.type === VOUCHER_TYPE_PERSONAL && (
            <>
              <UserReferenceInput readOnly={type === UPDATE} />
              <OrganisationReferenceInput readOnly={!!currentUserRestrictedToOrganisationId || type === UPDATE} />
            </>
          )
        }
      </FormDataConsumer>

      <FormDivider />

      <SelectInput
        source="discount_type"
        choices={voucherDiscountTypeChoices}
        validate={required()}
        readOnly={type === UPDATE && hasBeenUsed}
      />

      <FormDataConsumer>
        {({ formData }) => {
          const config = discountValueConfig[formData.discount_type]
          return (
            <AdvancedNumberInput
              source="discount_value"
              readOnly={type === UPDATE && hasBeenUsed}
              min={config.minValue}
              max={config.maxValue}
              validate={required()}
              parse={(v) => (formData.discount_type === VOUCHER_DISCOUNT_TYPE_MINUTE ? v * 60 : v)}
              format={(v) => (formData.discount_type === VOUCHER_DISCOUNT_TYPE_MINUTE ? v / 60 : v)}
              unit={config.unit}
            />
          )
        }}
      </FormDataConsumer>

      <FormDivider />

      <AdvancedDateTimeInput
        readOnly={
          type === UPDATE &&
          (hasBeenUsed ||
            parseDateAsDateTime(record.start_date) < parseDateAsDateTime(Date.now()).minus({ minutes: 5 }))
        }
        source="start_date"
        validate={required()}
      />

      <FormDataConsumer>
        {({ formData }) => (
          <AdvancedDateTimeInput
            minDate={formData.start_date ? parseDateAsDateTime(formData.start_date) : undefined}
            source="end_date"
            validate={(v) => validateEndDateAfterStartDate(formData.start_date, v)}
          />
        )}
      </FormDataConsumer>
    </AdvancedSimpleForm>
  )
}

export default VoucherFormLayout
